import { NavLink } from 'react-router-dom';
import type { ComponentType } from 'react';
import { useMe } from './Session';
import { Mark } from './Mark';
import {
  IconAdmin, IconAircraft, IconAsk, IconCash, IconCompare, IconDocuments,
  IconBanking, IconExpenses, IconLedger, IconOccupancy, IconApar, IconBeverage, IconBudget, IconPayroll, IconPulse, IconReports, IconTickets, IconSalesTax,
} from './icons';
import type { IconProps } from './icons';
import type { ModuleName } from '../lib/types';
import './shell.css';

export interface NavItem {
  to: string;
  label: string;
  /** Tab-bar label — one word. */
  short: string;
  icon: ComponentType<IconProps>;
  /** The module a user must hold to see this at all. Deny by default. */
  module: ModuleName;
  /** Show in the mobile tab bar rather than only the More sheet. */
  primary?: boolean;
}

/** The whole app, in order. Screens are registered as routes in App.tsx. */
export const NAV_ITEMS: NavItem[] = [
  { to: '/',          label: 'Pulse',     short: 'Pulse',   icon: IconPulse,     module: 'venue_ops', primary: true },
  { to: '/cash',      label: 'Cash & Close', short: 'Cash', icon: IconCash,      module: 'venue_ops', primary: true },
  { to: '/compare',   label: 'Compare',   short: 'Compare', icon: IconCompare,   module: 'venue_ops', primary: true },
  { to: '/expenses',  label: 'Expenses',  short: 'Expenses', icon: IconExpenses, module: 'expenses',  primary: true },
  { to: '/reports',   label: 'Reports',   short: 'Reports', icon: IconReports,   module: 'venue_ops' },
  { to: '/documents', label: 'Companies', short: 'Docs',    icon: IconDocuments, module: 'documents' },
  { to: '/occupancy', label: 'Occupancy', short: 'People',  icon: IconOccupancy, module: 'occupancy' },
  { to: '/aircraft',  label: 'Aircraft',  short: 'N278SC',  icon: IconAircraft,  module: 'aircraft' },
  { to: '/ask',       label: 'Ask',       short: 'Ask',     icon: IconAsk,       module: 'ai_ask' },
  { to: '/ledger',    label: 'Ledger',    short: 'Ledger',  icon: IconLedger,    module: 'ledger' },
  { to: '/banking',   label: 'Banking',   short: 'Bank',    icon: IconBanking,   module: 'banking' },
  { to: '/lineleap',  label: 'LineLeap',  short: 'LineLeap', icon: IconTickets,  module: 'venue_ops' },
  { to: '/tickets',   label: 'Tickets',   short: 'Tickets', icon: IconTickets,   module: 'venue_ops' },
  { to: '/payroll',   label: 'Payroll',   short: 'Payroll', icon: IconPayroll,   module: 'payroll' },
  { to: '/beverage',  label: 'Beverage',  short: 'Bev Cost',  icon: IconBeverage,  module: 'beverage_cost' },
  { to: '/salestax',   label: 'Sales Tax', short: 'Tax',     icon: IconSalesTax,   module: 'sales_tax' },
  { to: '/budget',     label: 'Budget',    short: 'Budget',  icon: IconBudget,    module: 'ledger' },
  { to: '/apar',       label: 'Bills & Invoices', short: 'AP/AR', icon: IconApar, module: 'ledger' },
  { to: '/admin',     label: 'Admin',     short: 'Admin',   icon: IconAdmin,     module: 'admin' },
];

/** Human title for the header, by path. */
export function titleFor(pathname: string): string {
  const exact = NAV_ITEMS.find((i) => i.to === pathname);
  if (exact) return exact.label;
  const nested = NAV_ITEMS.filter((i) => i.to !== '/').find((i) => pathname.startsWith(i.to));
  return nested?.label ?? 'Pulse';
}

/** The desktop rail. Hidden under 900px, where the tab bar takes over. */
export function Nav() {
  const { can } = useMe();

  return (
    <nav className="sh-rail" aria-label="Sections">
      <div className="sh-brand">
        <Mark size={52} label={false} className="sh-brand__mark" />
        <div>
          <div className="sh-brand__name">205 Holdings</div>
          <div className="sh-brand__sub">Tuscaloosa</div>
        </div>
      </div>

      <div className="sh-nav">
        {NAV_ITEMS.filter((i) => can(i.module)).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === '/'} className="sh-nav__link">
            <Icon className="sh-nav__icon" />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
